import { useState, useEffect, useRef } from 'react'
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

export default function VerifyEmail() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [token, setToken] = useState('')
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [registrationMessage, setRegistrationMessage] = useState<string | null>(null)

  const autoVerified = useRef(false)

  useEffect(() => {
    // Get email from navigation state (after registration)
    if (location.state && (location.state as any).email) {
      setEmail((location.state as any).email)
    }
    if (location.state && (location.state as any).message) {
      setRegistrationMessage((location.state as any).message)
      // Clear state after displaying
      window.history.replaceState({}, document.title)
    }

    // Get token from URL query parameter
    const urlToken = searchParams.get('token')
    if (urlToken) {
      setToken(urlToken)
    }
  }, [searchParams, location])

  // Auto-verify when token is present in URL
  useEffect(() => {
    const urlToken = searchParams.get('token')
    if (urlToken && !autoVerified.current && !success && !isLoading) {
      autoVerified.current = true
      setIsLoading(true)
      setError('')
      apiClient.verifyEmail(urlToken)
        .then(() => {
          setSuccess(true)
          setTimeout(() => {
            navigate('/login', { state: { message: 'Email verified successfully! Your account is pending administrator approval. You will be notified once approved.' } })
          }, 4000)
        })
        .catch((err: any) => {
          setError(err.message || 'Failed to verify email. The token may be invalid or expired.')
        })
        .finally(() => setIsLoading(false))
    }
  }, [searchParams, success, isLoading, navigate])

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess(false)

    if (!token) {
      setError('Verification token is required')
      return
    }

    setIsLoading(true)

    try {
      await apiClient.verifyEmail(token)
      setSuccess(true)
      // Redirect to login after 4 seconds
      setTimeout(() => {
        navigate('/login', { state: { message: 'Email verified successfully! Your account is pending administrator approval. You will be notified once approved.' } })
      }, 4000)
    } catch (err: any) {
      setError(err.message || 'Failed to verify email. The token may be invalid or expired.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setResendSuccess(false)

    if (!email) {
      setError('Email address is required')
      return
    }

    setIsResending(true)

    try {
      await apiClient.resendVerificationEmail(email)
      setResendSuccess(true)
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification email')
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="min-h-screen bg-background dark flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5"></div>
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent"></div>
      
      <Card className="relative z-10 w-full max-w-md border-border shadow-2xl">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-primary-foreground" />
            </div>
            <h1 className="text-xl font-bold text-primary">Sycek OSINT</h1>
          </div>
          <CardTitle className="text-foreground">Verify Your Email</CardTitle>
          <CardDescription className="text-muted-foreground">
            Enter the verification token from your email to activate your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {registrationMessage && (
            <div className="text-sm text-green-600 bg-green-500/10 border border-green-500/20 rounded-md p-3 mb-4 flex items-start gap-2">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{registrationMessage}</span>
            </div>
          )}
          {success ? (
            <div className="space-y-4">
              <div className="text-sm text-green-600 bg-green-500/10 border border-green-500/20 rounded-md p-4 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium mb-1">Email verified successfully!</p>
                  <p className="text-sm">Your account is now pending administrator approval. You will receive an email once your account has been approved.</p>
                  <p className="text-xs text-muted-foreground mt-2">Redirecting to login page...</p>
                </div>
              </div>
              <Link to="/login">
                <Button className="w-full">
                  Go to Login
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              <form onSubmit={handleVerify} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="token" className="text-sm font-medium text-foreground">
                    Verification Token
                  </label>
                  <Input
                    id="token"
                    type="text"
                    placeholder="Paste the verification token from your email"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Check your email inbox for the verification token and paste it here
                  </p>
                </div>

                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={isLoading || !token}>
                  {isLoading ? 'Verifying...' : 'Verify Email'}
                </Button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <form onSubmit={handleResend} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium text-foreground">
                    Resend Verification Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Didn't receive the email? Enter your email to resend the verification token
                  </p>
                </div>

                {resendSuccess && (
                  <div className="text-sm text-green-600 bg-green-500/10 border border-green-500/20 rounded-md p-3">
                    Verification email sent! Please check your inbox.
                  </div>
                )}

                <Button type="submit" variant="outline" className="w-full" disabled={isResending || !email}>
                  {isResending ? 'Sending...' : 'Resend Verification Email'}
                </Button>
              </form>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-primary hover:underline flex items-center justify-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

